import { BodyOf, defineRoute, defineRoute$, ResponseOf } from "./typedRoute";
import type { ProductCollectionItem } from "./types";
import type { IUserinfo } from "@store/index";

export const fetchProductList = defineRoute("GET/api/focus/products-collection").as<ProductCollectionItem[]>();

export const fetchCreateOption = defineRoute("GET/api/video/creation-option");
export type FetchCreateOptionResponse = NonNullable<ResponseOf<"GET/api/video/creation-option">>;

export const fetchRemakeOption = defineRoute("GET/api/video/remake-option");
export type FetchRemakeOptionResponse = NonNullable<ResponseOf<"GET/api/video/remake-option">>;

export const fetchParseProductImages = defineRoute("POST/api/product/parse-info-by-id", {
  timeoutMs: 180_000,
});
export type FetchParseProductImagesResponse = ResponseOf<"POST/api/product/parse-info-by-id">;

export const getProfile = defineRoute("GET/api/users/profile", { context: { silent: true } }).as<IUserinfo>();

/** 获取授权登录url */
export const creatorAuthUrl = defineRoute("GET/api/tiktok-shop/creator-auth-url");

/** 分页查询用户可选产品列表 */
export const listAccountProducts = defineRoute("GET/api/tiktok-shop/account-products");

/** 关联账号列表 */
export const getLinkAccounts = defineRoute("GET/api/users/link/accounts");

/** 解绑关联账号 */
export const userLinkUnbind = defineRoute("POST/api/users/link/unbind");

/** 提交视频发布 */
export const submitPublish = defineRoute("POST/api/users/publish/submit");
export type SubmitPublishResponse = ResponseOf<"POST/api/users/publish/submit">;
export type UserPublishSubmitRequest = BodyOf<"POST/api/users/publish/submit">;

/** 确认视频发布 */
export const confirmPublish = defineRoute("POST/api/users/publish/confirm");

/** 查询视频发布结果 */
export const queryPublishResult = defineRoute("GET/api/users/publish/result");
export const queryPublishResult$ = defineRoute$("GET/api/users/publish/result");
export type UserPublishResultResponse = ResponseOf<"GET/api/users/publish/result">;

/** 分页查询视频发布记录 */
export const queryPublishPage = defineRoute("GET/api/users/publish/page");
export const queryPublishPage$ = defineRoute$("GET/api/users/publish/page");
export type UserPublishPageRequest = NonNullable<Parameters<typeof queryPublishPage>[0]>;
export type PublishPageResponse = ResponseOf<"GET/api/users/publish/page">;

/** 取消预约发布 */
export const cancelPublish = defineRoute("POST/api/users/publish/cancel");

/** 查询视频关联的产品信息 */
export const queryAssetProductInfo = defineRoute("GET/api/tiktok-shop/asset-product");
export type AssetProductInfoResponse = ResponseOf<"GET/api/tiktok-shop/asset-product">;

/** 分页查询用户可选产品列表 */
export const queryAccountProductList = defineRoute("GET/api/tiktok-shop/account-products");

/** 生成商品属性（卖点） */
export const generateProductSellingPoints = defineRoute("POST/api/product/attribute");

// 自定义商品：根据图片和关键词推断卖点
export const getInferSellingPoints = defineRoute("POST/api/product/infer-selling-point", { timeoutMs: 120_000 });

export const clipLogin = defineRoute("POST/api/login");

export const getPreviewVideoCredit = defineRoute("POST/api/video/creation/credit-preview");

/** @deprecated 请使用 getUserAssetsV2，仅 videoRemake 等遗留模块仍在使用 */
export const getUserAssets = defineRoute("GET/api/users/assets");
/** @deprecated 请使用 UserAssetV2Record */
export type UserAssetRecord = NonNullable<ResponseOf<"GET/api/users/assets">["data"]>[number];

export const getUserAssetsV2 = defineRoute("GET/api/user-assets");
export type UserAssetsV2PageResponse = NonNullable<ResponseOf<"GET/api/user-assets">>;
export type UserAssetV2Record = NonNullable<NonNullable<UserAssetsV2PageResponse>["data"]>[number];

export const getUserAssetDetail = defineRoute("GET/api/user-assets/detail");
export type UserAssetDetail = NonNullable<ResponseOf<"GET/api/user-assets/detail">>;

export const getTrashAssets = defineRoute("GET/api/user-assets/trash");
export type UserAssetTrashItemRecord = NonNullable<ResponseOf<"GET/api/user-assets/trash">>[number];

/** 上传用户手动选择的 mp4/mov 视频（最大 100MB） */
export type UploadUserVideoResponse = ResponseOf<"POST/api/user-assets/upload-video">;

export const videoGenerateStatus = defineRoute("POST/api/video/script-generate");
export type VideoGenerateStatusRequest = BodyOf<"POST/api/video/script-generate">;

// ===== Account / Configurations =====

/** 登出当前会话 */
export const logout = defineRoute("POST/api/logout");

/** 修改用户资料 */
export const modifyProfile = defineRoute("POST/api/users/profile");

/** 媒体上传协议（查询） */
export const getMediaAgreement = defineRoute("GET/api/users/media-agreement");

/** 媒体上传协议（确认） */
export const confirmMediaAgreement = defineRoute("POST/api/users/media-agreement");

/** 校验 Kalo OAuth Token */
export const verifyKaloOauthToken = defineRoute("POST/api/oauth2/kalo");

/** 获取后端配置项（按 key 列表批量查询） */
export const fetchConfigurations = defineRoute("POST/api/configurations");

/** 翻译标题（POST /api/translation/translate） */
export const translateTitle = defineRoute("POST/api/translation/translate");

// ===== Captcha =====

/** 发送短信验证码 */
export const sendSmsCaptcha = defineRoute("POST/api/captcha/sms-send");

/** 发送邮件验证码 */
export const sendEmailCaptcha = defineRoute("POST/api/captcha/email-send");

// ===== Pay =====

/** 查询订单信息 */
export const queryOrder = defineRoute("GET/api/pay/queryOrder");

// ===== Video Workshop =====

/** 视频脚本分析（GET 轮询结果） */
export const fetchScriptAnalysisResult = defineRoute("GET/api/video/script-analysis");

/** 视频脚本分析（POST 触发任务，body+query 组合） */
export const submitScriptAnalysisTask = defineRoute("POST/api/video/script-analysis");

/** 视频脚本生成结果（GET 轮询） */
export const fetchScriptGenerationResult = defineRoute("GET/api/video/script-generate");

/** 视频参考脚本生成（长超时） */
export const fetchReferenceScript = defineRoute("POST/api/video/reference-script", { timeoutMs: 180_000 });

/** 视频链接预览 */
export const fetchVideoLinkPreview = defineRoute("POST/api/video/link-preview", { context: { silent: true } });

/** 视频生成（创建任务） */
export const createVideoCreation = defineRoute("POST/api/video/creation");

/** 视频生成（重新提交任务） */
export const recreateVideoCreation = defineRoute("POST/api/video/recreation");

/** 视频任务详情（创作/复刻共用，按 jobId 查询） */
export const fetchVideoJobDetail = defineRoute("GET/api/video/job-detail");

/** 视频任务详情的 Observable 版本（用于 RxJS 串行轮询，见 library 卡片自轮询） */
export const fetchVideoJobDetail$ = defineRoute$("GET/api/video/job-detail");

/** 视频复刻（创建任务） */
export const createVideoRemake = defineRoute("POST/api/video/remake");

/** 视频复刻积分试算 */
export const fetchVideoRemakeCreditPreview = defineRoute("POST/api/video/remake/credit-preview");

/** 视频复刻任务详情（按 jobId 查询，与 fetchVideoJobDetail 同一路由） */
export const fetchVideoRemakeJobDetail = defineRoute("GET/api/video/job-detail");

/** 视频信息（detail） */
export const fetchVideoDetail = defineRoute("GET/api/video/detail");

/** 商品信息（按视频上下文） */
export const fetchProductInfo = defineRoute("GET/api/video/product-info");

/** 视频可下载 URL */
export const fetchVideoUrl = defineRoute("GET/api/video/video-url");

/** 视频下载链接 */
export const fetchVideoDownloadUrl = defineRoute("GET/api/video/download-url");

// ===== Product =====

/** 商品卖点查询（GET 轮询） */
export const fetchProductSellingPointResult = defineRoute("GET/api/product/attribute");

/** 商品详情（按 id + country） */
export const fetchProductDetail = defineRoute("POST/api/product/detail");

/** 商品图片合规检测（长超时） */
export const fetchAiAnalyzeImage = defineRoute("POST/api/product/image-check-and-describe", { timeoutMs: 180_000 });

/** 按链接解析商品信息（长超时 + silent，错误由调用侧处理） */
export const fetchParseInfoByLink = defineRoute("POST/api/product/parse-info-by-link", {
  timeoutMs: 180_000,
  context: { silent: true },
});

/** 通过 URL 上传商品图片（长超时） */
export const fetchUploadImagesByUrl = defineRoute("POST/api/video/upload-images-by-url", { timeoutMs: 180_000 });

/** 商品搜索 */
export const searchProductByKeyword = defineRoute("GET/api/product/keyword-search");

// ===== User Products (商品主体库 / Created) =====

/** 我创建的商品主体列表（pageNo / pageSize） */
export const fetchUserProductList = defineRoute("GET/api/users/collection/products");
export type UserProductRecord = NonNullable<ResponseOf<"GET/api/users/collection/products">["data"]>[number];

// TODO: 详情页接入时启用
export const fetchUserProductDetail = defineRoute("GET/api/users/collection/products/detail");

// TODO: 创建商品主体表单接入时启用
export const createUserProduct = defineRoute("POST/api/users/collection/products");

// TODO: 编辑商品主体表单接入时启用
export const updateUserProduct = defineRoute("PUT/api/users/collection/products");

// TODO: 删除商品主体（软删除）接入时启用
export const deleteUserProduct = defineRoute("DELETE/api/users/collection/products");

// ===== Character Assets (角色资产库 / Models / Animals) =====

/** 角色资产列表（按 category 过滤，分页 pageNo/pageSize） */
export const listCharacterAssets = defineRoute("GET/api/character-assets");
export type CharacterAssetRecord = NonNullable<NonNullable<ResponseOf<"GET/api/character-assets">>["data"]>[number];

/** 角色资产详情（按 assetId） */
export const getCharacterAssetDetail = defineRoute("GET/api/character-assets/detail");

/** 角色资产创建（multipart 一次提交：category / name / mainImage / otherImages） */
export const createCharacterAsset = defineRoute("POST/api/character-assets", { timeoutMs: 120_000 });

/** 角色资产改名（仅 name 字段，按 assetId） */
export const renameCharacterAsset = defineRoute("PUT/api/character-assets");

/** 角色资产删除（软删除，按 assetId） */
export const deleteCharacterAsset = defineRoute("DELETE/api/character-assets");

// ===== Credits =====

/** AI 权益余额 */
export const fetchAiCreditsBalance = defineRoute("GET/api/credits/balance");

/** AI 权益是否消耗（按 id+type 查询） */
export const fetchAiCreditsCheckConsumed = defineRoute("GET/api/credits/check-consumed");

/** 用户权益使用记录（分页） */
export const fetchAiCreditsLogs = defineRoute("GET/api/credits/logs");

// ===== Library / Collection / Trash / Asset / Report =====

/** 收藏列表查询 */
export const fetchCollectionList = defineRoute("POST/api/users/collection/queryList");

/** 收藏详情查询 */
export const fetchCollectionDetail = defineRoute("GET/api/users/collection/queryDetail");

/** 收藏保存 */
export const saveCollection = defineRoute("POST/api/users/collection/save");

/** 收藏删除 */
export const deleteCollection = defineRoute("GET/api/users/collection/delete");

/** 收藏状态查询 */
export const fetchCollectionStatus = defineRoute("POST/api/users/collection/status");

/** 取消关注实体 */
export const cancelFocusEntity = defineRoute("POST/api/focus/cancelEntity");

/** 回收站列表 */
export const fetchTrashList = defineRoute("GET/api/users/trash");

/** 回收站列表 V2（VID 封面/时长走 vidExtra） */
export const fetchTrashListV2 = defineRoute("GET/api/user-assets/trash");
export type UserAssetTrashRecord = NonNullable<NonNullable<ResponseOf<"GET/api/user-assets/trash">>[number]>;

/** 移入回收站（已废弃，请使用 moveAssetsToTrash） */
export const moveToTrash = defineRoute("POST/api/users/trash");

/** 批量移入回收站（新接口，替代 POST /api/users/trash） */
export const moveAssetsToTrash = defineRoute("POST/api/user-assets/trash");
export type MoveAssetsToTrashRequest = BodyOf<"POST/api/user-assets/trash">;

/** 回收站永久删除 */
export const deleteFromTrash = defineRoute("DELETE/api/users/trash");

/** 清空回收站 */
export const emptyTrash = defineRoute("DELETE/api/users/trash/empty");

/** 从回收站恢复 */
export const restoreFromTrash = defineRoute("POST/api/users/trash/restore");

/** AI 任务重试 */
export const resubmitAiJob = defineRoute("POST/api/users/ai-jobs/resubmit");

/** 问题报告退款配额 */
export const fetchRefundQuota = defineRoute("GET/api/report/refund-quota");

/** 资产是否已下载 */
export const fetchAssetDownloaded = defineRoute("GET/api/asset/downloaded");

/** 资产状态 */
export const fetchAssetState = defineRoute("GET/api/asset/state");

/** 资产下载上报 */
export const recordAssetDownload = defineRoute("POST/api/asset/record-download");

/** 提交问题报告 */
export const submitReport = defineRoute("POST/api/report/submit");

export const getVideoCreationJobDetail = defineRoute("GET/api/video/creation/job-detail");

/** Contact Us 联系方式（按用户 biz_country 匹配，未命中回退默认国家） */
export const getSupportContacts = defineRoute("GET/api/support/contacts");
export type SupportContactsResponse = NonNullable<ResponseOf<"GET/api/support/contacts">>;

/** 美元汇率换算（积分购买近似展示） */
export const convertExchangeRate = defineRoute("GET/api/exchange-rate/convert");
export const convertExchangeRate$ = defineRoute$("GET/api/exchange-rate/convert", { context: { silent: true } });
export type ExchangeRateConvertResponse = NonNullable<ResponseOf<"GET/api/exchange-rate/convert">>;
